/**
 * Insurance Payout API Client
 * Handles all API operations for the Insurance Payout System
 */

import api from './api';

// Types
export type InsuranceProvider = {
  id: number;
  name: string;
  provider_type: 'xactimate' | 'symbility' | 'simsol' | 'custom';
  version?: string;
  effective_date: string;
  is_active: boolean;
  notes?: string;
  payout_items_count?: number;
  created_at: string;
  updated_at: string;
};

export type InsurancePayoutItem = {
  id: number;
  provider: number;
  provider_name?: string;
  category: string;
  selector_code: string;
  description: string;
  unit_of_measure: string;
  replace_price?: string | null;
  remove_price?: string | null;
  detach_reset_price?: string | null;
  primary_price?: string;
  supporting_info?: string | null;
  is_active: boolean;
  mapped_distributors_count?: number;
  last_updated: string;
  created_at: string;
  updated_at: string;
};

export type InsuranceItemMapping = {
  id: number;
  insurance_item: number;
  insurance_selector?: string;
  insurance_description?: string;
  insurance_price?: string;
  price_list_item: number;
  distributor_name?: string;
  product_name?: string;
  product_price?: string;
  material_spec?: number | null;
  material_spec_name?: string | null;
  conversion_factor: string;
  is_preferred: boolean;
  mapping_confidence: string;
  notes?: string;
  margin_data?: {
    margin_amount: string;
    margin_percent: number;
    insurance_price: string;
    distributor_price: string;
  } | null;
  created_by?: number | null;
  verified_at?: string | null;
  created_at: string;
  updated_at: string;
};

export type MappingSuggestion = {
  id: number;
  distributor: string;
  product_name: string;
  unit_price: string;
  unit_of_measure: string;
  score: number;
  potential_margin: number | null;
};

// API Response Types
export interface ListResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

export interface UploadPreviewResponse {
  message: string;
  provider: InsuranceProvider;
  preview: any[];
  all_items: any[];
  total_items: number;
  metadata: any;
}

export interface BulkImportResponse {
  message: string;
  created: number;
  updated: number;
}

export interface BestPriceResponse {
  insurance_price: string;
  best_distributor: {
    name: string;
    product: string;
    price: string;
    margin: any;
  } | null;
  message?: string;
}

export interface MappingSuggestionsResponse {
  insurance_item: {
    id: number;
    selector_code: string;
    description: string;
    unit: string;
    price: string;
  };
  suggestions: MappingSuggestion[];
}

// Insurance Provider API
export const insuranceProviderApi = {
  list: async (params?: { is_active?: boolean; provider_type?: string; search?: string }): Promise<ListResponse<InsuranceProvider>> => {
    const queryParams = new URLSearchParams();
    if (params?.is_active !== undefined) queryParams.append('is_active', params.is_active.toString());
    if (params?.provider_type) queryParams.append('provider_type', params.provider_type);
    if (params?.search) queryParams.append('search', params.search);
    
    const response = await api.get<ListResponse<InsuranceProvider>>(`/api/distributors/insurance-providers/?${queryParams}`);
    return response.data;
  },

  get: async (id: number): Promise<InsuranceProvider> => {
    const response = await api.get<InsuranceProvider>(`/api/distributors/insurance-providers/${id}/`);
    return response.data;
  },

  create: async (data: Partial<InsuranceProvider>): Promise<InsuranceProvider> => {
    const response = await api.post<InsuranceProvider>('/api/distributors/insurance-providers/', data);
    return response.data;
  },

  update: async (id: number, data: Partial<InsuranceProvider>): Promise<InsuranceProvider> => {
    const response = await api.patch<InsuranceProvider>(`/api/distributors/insurance-providers/${id}/`, data);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/api/distributors/insurance-providers/${id}/`);
  },

  activate: async (id: number): Promise<{ status: string }> => {
    const response = await api.post<{ status: string }>(`/api/distributors/insurance-providers/${id}/activate/`, {});
    return response.data;
  },

  deactivate: async (id: number): Promise<{ status: string }> => {
    const response = await api.post<{ status: string }>(`/api/distributors/insurance-providers/${id}/deactivate/`, {});
    return response.data;
  },

  duplicate: async (id: number): Promise<InsuranceProvider> => {
    const response = await api.post<InsuranceProvider>(`/api/distributors/insurance-providers/${id}/duplicate/`, {});
    return response.data;
  }
};

// Insurance Payout Item API
export const insurancePayoutApi = {
  list: async (params?: {
    provider?: number;
    category?: string;
    is_active?: boolean;
    unmapped?: boolean;
    multiple_distributors?: boolean;
    search?: string;
    page?: number;
  }): Promise<ListResponse<InsurancePayoutItem>> => {
    const queryParams = new URLSearchParams();
    if (params?.provider) queryParams.append('provider', params.provider.toString());
    if (params?.category) queryParams.append('category', params.category);
    if (params?.is_active !== undefined) queryParams.append('is_active', params.is_active.toString());
    if (params?.unmapped) queryParams.append('unmapped', 'true');
    if (params?.multiple_distributors) queryParams.append('multiple_distributors', 'true');
    if (params?.search) queryParams.append('search', params.search);
    if (params?.page && params.page > 1) queryParams.append('page', params.page.toString());
    
    const response = await api.get<ListResponse<InsurancePayoutItem>>(`/api/distributors/insurance-payout-items/?${queryParams}`);
    return response.data;
  },

  get: async (id: number): Promise<InsurancePayoutItem> => {
    const response = await api.get<InsurancePayoutItem>(`/api/distributors/insurance-payout-items/${id}/`);
    return response.data;
  },

  create: async (data: Partial<InsurancePayoutItem>): Promise<InsurancePayoutItem> => {
    const response = await api.post<InsurancePayoutItem>('/api/distributors/insurance-payout-items/', data);
    return response.data;
  },

  update: async (id: number, data: Partial<InsurancePayoutItem>): Promise<InsurancePayoutItem> => {
    const response = await api.patch<InsurancePayoutItem>(`/api/distributors/insurance-payout-items/${id}/`, data);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/api/distributors/insurance-payout-items/${id}/`);
  },

  uploadFile: async (
    file: File,
    providerId?: number,
    providerName?: string,
    providerType?: string,
    version?: string,
    replaceExisting: boolean = false
  ): Promise<UploadPreviewResponse> => {
    const formData = new FormData();
    formData.append('file', file);
    if (providerId) formData.append('provider_id', providerId.toString());
    if (providerName) formData.append('provider_name', providerName);
    if (providerType) formData.append('provider_type', providerType);
    if (version) formData.append('version', version);
    formData.append('replace_existing', replaceExisting.toString());
    
    const response = await api.post<UploadPreviewResponse>('/api/distributors/insurance-payout-items/upload_file/', formData);
    return response.data;
  },

  bulkImport: async (
    providerId: number,
    items: any[],
    replaceExisting: boolean = false
  ): Promise<BulkImportResponse> => {
    const response = await api.post<BulkImportResponse>('/api/distributors/insurance-payout-items/bulk_import/', {
      provider_id: providerId,
      items: items,
      replace_existing: replaceExisting,
    });
    return response.data;
  },

  getMappings: async (id: number): Promise<InsuranceItemMapping[]> => {
    const response = await api.get<InsuranceItemMapping[]>(`/api/distributors/insurance-payout-items/${id}/mappings/`);
    return response.data;
  },

  getBestPrice: async (id: number): Promise<BestPriceResponse> => {
    const response = await api.get<BestPriceResponse>(`/api/distributors/insurance-payout-items/${id}/best_price/`);
    return response.data;
  },

  getCategories: async (): Promise<string[]> => {
    const response = await api.get<{ categories: string[] }>('/api/distributors/insurance-payout-items/categories/');
    return response.data.categories;
  }
};

// Insurance Item Mapping API
export const insuranceMappingApi = {
  list: async (params?: {
    insurance_item?: number;
    distributor?: number;
    is_preferred?: boolean;
  }): Promise<ListResponse<InsuranceItemMapping>> => {
    const queryParams = new URLSearchParams();
    if (params?.insurance_item) queryParams.append('insurance_item', params.insurance_item.toString());
    if (params?.distributor) queryParams.append('price_list_item__distributor', params.distributor.toString());
    if (params?.is_preferred !== undefined) queryParams.append('is_preferred', params.is_preferred.toString());
    
    const response = await api.get<ListResponse<InsuranceItemMapping>>(`/api/distributors/insurance-mappings/?${queryParams}`);
    return response.data;
  },

  get: async (id: number): Promise<InsuranceItemMapping> => {
    const response = await api.get<InsuranceItemMapping>(`/api/distributors/insurance-mappings/${id}/`);
    return response.data;
  },

  create: async (data: {
    insurance_item: number;
    price_list_item: number;
    material_spec?: number;
    conversion_factor?: string;
    is_preferred?: boolean;
    notes?: string;
  }): Promise<InsuranceItemMapping> => {
    const response = await api.post<InsuranceItemMapping>('/api/distributors/insurance-mappings/', data);
    return response.data;
  },

  update: async (id: number, data: Partial<InsuranceItemMapping>): Promise<InsuranceItemMapping> => {
    const response = await api.patch<InsuranceItemMapping>(`/api/distributors/insurance-mappings/${id}/`, data);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/api/distributors/insurance-mappings/${id}/`);
  },

  bulkCreate: async (
    insuranceItemId: number,
    priceListItemIds: number[],
    setPreferred?: number
  ): Promise<{ message: string; mappings: InsuranceItemMapping[] }> => {
    const response = await api.post<{ message: string; mappings: InsuranceItemMapping[] }>('/api/distributors/insurance-mappings/bulk_create/', {
      insurance_item_id: insuranceItemId,
      price_list_item_ids: priceListItemIds,
      set_preferred: setPreferred,
    });
    return response.data;
  },

  suggestMappings: async (
    insuranceItemId: number,
    limit: number = 10
  ): Promise<MappingSuggestionsResponse> => {
    const response = await api.post<MappingSuggestionsResponse>('/api/distributors/insurance-mappings/suggest_mappings/', {
      insurance_item_id: insuranceItemId,
      limit: limit,
    });
    return response.data;
  },

  setPreferred: async (id: number): Promise<InsuranceItemMapping> => {
    const response = await api.post<InsuranceItemMapping>(`/api/distributors/insurance-mappings/${id}/set_preferred/`, {});
    return response.data;
  },

  verify: async (id: number): Promise<InsuranceItemMapping> => {
    const response = await api.post<InsuranceItemMapping>(`/api/distributors/insurance-mappings/${id}/verify/`, {});
    return response.data;
  }
};

// Export all APIs as a single object for convenience
export const insuranceApi = {
  providers: insuranceProviderApi,
  payoutItems: insurancePayoutApi,
  mappings: insuranceMappingApi,
};